import { useQuery } from "@tanstack/react-query";
import type { LoginHistoryPayload } from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  configurationRequest
} from "./configurationRequest";

/**
 * 25 in this UI. CC9's own default is 50; nothing in the spec depends on the number, and 25 keeps a
 * page scannable without scrolling.
 */
export const LOGIN_HISTORY_PAGE_SIZE = 25;

export interface UseGetLoginHistoryOptions {
  userId?: string;
  accountId?: string;
  /**
   * **Zero-based — the wire's convention, forwarded to CC9 unchanged.**
   *
   * Not verified against a subject that actually has sign-ins, because none exists on nonprod (every
   * subject sampled 2026-08-21 returned `totalCount: 0`). The evidence for zero-based is CC9's own
   * default: the observed empty response came back `page: 0` when no `page` param was sent at all.
   * The UI counts from 1, so `LoginHistoryTab` converts — one place, clearly marked, because an
   * off-by-one here silently skips or repeats a page rather than failing.
   */
  page?: number;
  size?: number;
}

/**
 * Reads one page of the subject's sign-in history (FR-037, FR-038).
 *
 * **Server-paginated.** The BFF forwards `page`/`size` to CC9 and returns exactly one CC9 page — it
 * does not aggregate. So the response's `totalCount` / `totalPages` are the only source of truth for
 * how much history exists; the length of `loginHistory` describes the current page and nothing more.
 *
 * **`retry: 1` is a ceiling, not a default to raise.** This endpoint measured 17–22 seconds, plus one
 * `504` at 29.4s, to return 115 bytes — the latency is inside CC9 and is unrelated to page size. A
 * retry against a 20s endpoint doubles the wait without fixing anything. Accepted as an upstream fix
 * expected before prod; today it would exceed the 4s prod CC9 bound, so **a green nonprod run proves
 * nothing about prod for this endpoint.**
 */
export const useGetLoginHistory = ({
  userId,
  accountId,
  page = 0,
  size = LOGIN_HISTORY_PAGE_SIZE
}: UseGetLoginHistoryOptions) =>
  useQuery({
    // `page` and `size` belong in the key: without them, paging forward would serve the previous
    // page's rows straight from cache.
    queryKey: [
      CONFIGURATION_QUERY_KEY_BASE,
      "loginhistory",
      accountId,
      userId,
      page,
      size
    ],
    queryFn: () => {
      if (!userId || !accountId) {
        throw new Error(
          "useGetLoginHistory requires both userId and accountId"
        );
      }

      return configurationRequest<LoginHistoryPayload>({
        resource: "loginhistory",
        userId,
        accountId,
        // `configurationRequest` omits only `undefined`, so a numeric 0 survives — which matters,
        // since page 0 is a real page and dropping it would silently request CC9's default instead.
        params: { page, size }
      });
    },
    enabled: Boolean(userId && accountId),
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
